import { useEffect, useState } from 'react';
import { PreviewIframeMessage, previewMessageBusSubject, createPreviewMessageEventHandler } from '@shared/utils/previewMessageBus';
import { isInIframe } from '@shared/utils/isInIframe';
import globalWindow from '@shared/core/globals';
import { DesignLayoutType, WebsiteHeaderPresentationSettingsFragment } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import equals from 'deep-equal';

type UseWebsiteHeaderUpdatesListenerArgs = {
  initialValues: {
    activeHeaderPresentationLayout: Maybe<WebsiteHeaderPresentationSettingsFragment>;
    websiteLayout: Maybe<DesignLayoutType>;
  };
  refetch: () => void;
};

const useWebsiteHeaderUpdatesListener = (args: UseWebsiteHeaderUpdatesListenerArgs) => {
  const { initialValues, refetch } = args;
  const isPreviewing = isInIframe();

  // Opting to store override in memory as opposed to refetching the query
  const [overrides, setOverrides] = useState<{
    activeHeaderPresentationLayout: Maybe<WebsiteHeaderPresentationSettingsFragment>;
    websiteLayout: Maybe<DesignLayoutType>;
  }>(() => {
    return {
      activeHeaderPresentationLayout: initialValues.activeHeaderPresentationLayout,
      websiteLayout: initialValues.websiteLayout,
    };
  });

  const processMessage = useEventCallback((message: PreviewIframeMessage) => {
    switch (message.action) {
      case 'fromWebsiteDesignerPayload': {
        const updatedPresentationLayout = message.value.eventDesign.activeWebsiteHeaderPresentationLayout;
        const updatedWebsiteLayout = message.value.eventDesign.websiteLayout.layoutType;
        if (!equals(updatedPresentationLayout, overrides.activeHeaderPresentationLayout) || updatedWebsiteLayout !== overrides.websiteLayout) {
          setOverrides({
            websiteLayout: updatedWebsiteLayout,
            activeHeaderPresentationLayout: updatedPresentationLayout,
          });
        }
        break;
      }
      case 'fromList':
        const shouldRefetch = message.value.some(value => value.key === 'eventDisplayName' || value.key === 'greetings');
        if (shouldRefetch) {
          refetch();
        }
        break;
      default:
        break;
    }
  });

  useEffect(() => {
    if (!isPreviewing) {
      return;
    }

    const previewSub = previewMessageBusSubject.subscribe({
      next: action => {
        if (action.type === 'sendMessage') {
          processMessage(action.payload.message);
        }
      },
    });

    const receiveMessage = createPreviewMessageEventHandler(processMessage);
    globalWindow.addEventListener?.('message', receiveMessage, false);

    return () => {
      previewSub?.unsubscribe();
      globalWindow.removeEventListener?.('message', receiveMessage, false);
    };
  }, [isPreviewing, processMessage, refetch]);

  return { overrides };
};

export default useWebsiteHeaderUpdatesListener;
